/**
 * Saved views of a table: shared (everybody who can view the database) and personal (the
 * creator's). A view only remembers how to LOOK at the table; opening a table never needs one.
 * The server decides who may change what (`my` rules in API.md); `can_edit` is what the UI offers.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';

import { isCompleteFilter } from './cells';
import type {
  CardFieldsMode,
  PinnedLook,
  RecordFilter,
  SavedView,
  SortLevel,
  ViewConfig,
} from './types';

export const EMPTY_VIEW_CONFIG: ViewConfig = {
  filters: [],
  sort: [],
  hidden: [],
  order: [],
  mode: 'table',
  group_column_id: null,
  title_column_id: null,
  pinned: 'opaque',
  card_fields_mode: 'selected',
  card_fields: null,
};

/** The working state of the table as a config (only filters that are complete enough to run). */
export function toViewConfig(working: {
  filters: readonly RecordFilter[];
  sort: readonly SortLevel[];
  hidden: readonly string[];
  order: readonly string[];
  mode?: ViewConfig['mode'];
  group_column_id?: string | null;
  title_column_id?: string | null;
  pinned?: PinnedLook;
  show_sections?: boolean;
  card_fields_mode?: CardFieldsMode;
  card_fields?: readonly string[] | null;
}): ViewConfig {
  return {
    filters: working.filters.filter(isCompleteFilter).map((f) => ({
      column_id: f.column_id,
      op: f.op,
      ...(f.value !== undefined && f.value !== '' && { value: f.value }),
    })),
    sort: working.sort.map((s) => ({ column_id: s.column_id, direction: s.direction })),
    hidden: [...new Set(working.hidden)],
    order: [...new Set(working.order)],
    mode: working.mode ?? 'table',
    group_column_id: working.group_column_id ?? null,
    title_column_id: working.title_column_id ?? null,
    pinned: working.pinned ?? 'opaque',
    card_fields_mode: working.card_fields_mode ?? 'selected',
    card_fields: working.card_fields ? [...new Set(working.card_fields)] : null,
    // (only a hidden-sections choice is written; absent means shown, like views saved before)
    ...(working.show_sections === false && { show_sections: false }),
  };
}

const canonical = (config: ViewConfig): string =>
  JSON.stringify({
    filters: config.filters.map((f) => [f.column_id, f.op, f.value ?? null]),
    sort: config.sort.map((s) => [s.column_id, s.direction]),
    // Which columns are hidden does not depend on the order they were hidden in.
    hidden: [...config.hidden].sort(),
    order: config.order,
    mode: config.mode,
    group: config.group_column_id,
    title: config.title_column_id,
    pinned: config.pinned ?? 'opaque',
    sections: config.show_sections ?? true,
    cards: [config.card_fields_mode ?? 'selected', config.card_fields ?? null],
  });

/** Do two configs describe the same look? (what makes a view "modified") */
export const sameViewConfig = (a: ViewConfig, b: ViewConfig): boolean =>
  canonical(a) === canonical(b);

/** The columns in the order a view wants: listed ones first, the rest keep the table's order. */
export function orderColumns<T extends { id: string }>(
  columns: readonly T[],
  order: readonly string[],
): T[] {
  if (order.length === 0) return [...columns];
  const rank = new Map(order.map((id, index) => [id, index]));
  const listed = columns.filter((c) => rank.has(c.id));
  listed.sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0));
  return [...listed, ...columns.filter((c) => !rank.has(c.id))];
}

export function useViews(tableId: string) {
  return useQuery({
    queryKey: queryKeys.databases.views(tableId),
    queryFn: ({ signal }) =>
      api.get<SavedView[]>(`/v1/database-tables/${tableId}/views`, { signal }),
  });
}

function useRefresh(tableId: string) {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.databases.views(tableId) });
}

export function useCreateView(tableId: string) {
  const refresh = useRefresh(tableId);
  return useMutation({
    mutationFn: (input: { name: string; shared: boolean; config: ViewConfig }) =>
      api.post<SavedView>(`/v1/database-tables/${tableId}/views`, input),
    onSuccess: () => refresh(),
  });
}

export function useUpdateView(tableId: string) {
  const refresh = useRefresh(tableId);
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string;
      name?: string;
      config?: ViewConfig;
      expected_version?: number;
    }) => api.patch<SavedView>(`/v1/database-views/${id}`, patch),
    onSuccess: () => refresh(),
  });
}

export function useDuplicateView(tableId: string) {
  const refresh = useRefresh(tableId);
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; name: string; shared: boolean }) =>
      api.post<SavedView>(`/v1/database-views/${id}/duplicate`, input),
    onSuccess: () => refresh(),
  });
}

export function useDeleteView(tableId: string) {
  const refresh = useRefresh(tableId);
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/database-views/${id}`),
    onSuccess: () => refresh(),
  });
}
